import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiFetchBlob } from "./api";

// ── Types (mirror Allmark.Contracts.Datasheets) ─────────────────────────────
export interface SizeSlot { index: number; desc: string | null; productId: number | null; quantityAvail: number; unitPriceExGst: number | null; }
export interface DatasheetWarning { code: string; message: string; }

export interface DatasheetRow {
  datasheetId: number; companyId: number; companyName: string | null;
  datasheetTypeCode: string; datasheetTypeDescription: string | null;
  primaryContactId: number | null; primaryContactName: string | null;
  mode: string; totalRemaining: number; lastActivityUtc: string | null; hasWarnings: boolean;
}

export interface DatasheetPage { items: DatasheetRow[]; page: number; pageSize: number; totalCount: number; }

export interface DatasheetDetail {
  datasheetId: number; companyId: number; companyName: string | null; department: string | null;
  primaryContactId: number | null; primaryContactName: string | null;
  datasheetTypeCode: string; datasheetTypeDescription: string | null; priceTableId: number | null;
  isPrepaid: boolean; clientSuppliedStock: boolean; mode: string;
  tracksQuantity: boolean; pricesPerOrder: boolean;
  sizes: SizeSlot[];
  deliveryChargesAvail: number;
  prepaidFittingId: number | null; prepaidFittingQuantityAvail: number;
  prepaidFittingPriceExGst: number | null; prepaidFittingDesc: string | null;
  invoiceAddressId: number | null; invoiceTo: string | null; invoiceAttention: string | null;
  deliveryAddressId: number | null; deliveryTo: string | null; deliveryAttention: string | null;
  deliveryPhone: string | null; defaultDeliveryMethodId: number | null; defaultFittingId: number | null;
  doNotLabelAsAllmark: boolean; specialTurnaroundDays: number | null; deliveryConsignmentSize: number | null;
  spreadsheetTemplateFileId: number | null; prepaidExtraDesc: string | null; note: string | null;
  warnings: DatasheetWarning[];
  rowVersionBase64: string | null;
}

export interface DatasheetType {
  datasheetTypeCode: string; description: string; productTypeCode: string | null;
  defaultJobStatusCode: string | null; addOrderToWorkQueueCode: string | null;
  assignTaskToEmployeeId: number | null; rowVersionBase64: string | null;
}

export interface DatasheetAuthContact { datasheetAuthContactId: number; clientId: number; clientName: string | null; emailAddress: string | null; phone: string | null; }

export interface DatasheetPurchase {
  datasheetPurchaseId: number; purchaseDate: string; sizeQuantities: number[];
  deliveryChargeQuantity: number; prepaidFittingQuantity: number;
  delAmountPaid: number | null; quantityAmountPaid: number | null;
  invoiceId: number | null; jobId: number | null; clientPurchaseOrderNo: string | null;
  isAdjustment: boolean; adjustmentDesc: string | null; note: string | null;
}

export interface DatasheetOrder {
  datasheetUsageId: number; datasheetId: number; usageDate: string;
  requestedByContactId: number | null; requestedByContactName: string | null;
  sizeQuantities: number[]; deliveryChargeCount: number; prepaidFittingQuantity: number;
  jobId: number | null; jobReferenceNo: string | null; workQueueItemId: number | null;
  committed: boolean; dueDate: string | null; deliveryMethodId: number | null;
  deliveryAddressId: number | null; deliveryTo: string | null; deliveryAttention: string | null;
  doNotLabelAsAllmark: boolean; clientPurchaseOrderNo: string | null; note: string | null;
  rowVersionBase64: string | null;
}

export interface PriceBreakdownLine { description: string; quantity: number; unitPrice: number; lineTotal: number; }
export interface OrderPricing { totalExGst: number; lines: PriceBreakdownLine[]; }

export interface CommitOrderResult {
  order: DatasheetOrder; jobId: number; jobReferenceNo: string;
  workQueueItemId: number | null; pricing: OrderPricing | null; warnings: DatasheetWarning[];
}

export interface OrderBadgeInput {
  fieldValues: string[]; quantity: number; fittingId: number | null;
  deliveryTo: string | null; deliveryStreet: string | null; deliverySuburb: string | null; deliveryPostcode: string | null;
}
export interface DerivedOrderQuantities {
  sizeQuantities: number[]; deliveryChargeCount: number; prepaidFittingQuantity: number; warnings: string[];
}
export interface BuildOrderResult { draft: DatasheetOrder; derived: DerivedOrderQuantities; }
export interface OrderBadge {
  datasheetOrderBadgeId: number; artworkTemplateId: number; fieldValues: string[]; quantity: number;
  prepaidSize: number; prepaidUsageQuantity: number; fittingId: number | null;
  deliveryTo: string | null; deliveryStreet: string | null; deliverySuburb: string | null; deliveryPostcode: string | null; sortOrder: number;
}
export interface DeriveOrderBody { artworkTemplateId: number; badges: OrderBadgeInput[]; }
export interface BuildOrderBody extends DeriveOrderBody { requestedByContactId: number | null; note: string | null; }

export interface DatasheetRateBracket { rateType: string | null; rangeFrom: number; rangeTo: number; rangeDesc: string | null; rates: (number | null)[]; }
export interface DatasheetRates { priceTableId: number | null; sizes: SizeSlot[]; deliveryChargesAvail: number; prepaidFittingQuantityAvail: number; brackets: DatasheetRateBracket[]; }

export interface StatementLine {
  recordDate: string; recordType: string; reference: string | null;
  requestedByContactName: string | null; clientPurchaseOrderNo: string | null;
  sizeQuantities: number[]; deliveryChargeCount: number; prepaidFittingQuantity: number;
  delAmountPaid: number | null; quantityAmountPaid: number | null;
  excessDeliveryAmountDue: number | null; excessOrderAmountDue: number | null;
  sizeBalances: number[]; deliveryBalance: number; fittingBalance: number; adjustmentDesc: string | null;
}

export interface DatasheetStatement {
  datasheetId: number; companyName: string | null; sizes: SizeSlot[];
  from: string | null; to: string | null; lines: StatementLine[]; warnings: DatasheetWarning[];
}

export interface IssueStatementResult { blobPath: string; emailed: boolean; emailTo: string | null; }

export interface DatasheetSizeRow {
  datasheetId: number; companyId: number; companyName: string | null; datasheetTypeCode: string;
  sizeIndex: number; description: string | null; productId: number | null;
  quantityAvail: number; unitPriceExGst: number | null;
}

// ── Request bodies ──────────────────────────────────────────────────────────
export interface CreateDatasheetBody { companyId: number; datasheetTypeCode: string; department: string | null; primaryContactId: number | null; priceTableId: number | null; isPrepaid: boolean; clientSuppliedStock: boolean; sizes: SizeSlot[]; note: string | null; }
export type UpdateDatasheetBody = Partial<Omit<DatasheetDetail, "datasheetId" | "companyId" | "companyName" | "mode" | "tracksQuantity" | "pricesPerOrder" | "warnings" | "rowVersionBase64" | "datasheetTypeDescription" | "primaryContactName">>;
export interface CreatePurchaseBody { purchaseDate: string; sizeQuantities: number[]; deliveryChargeQuantity: number; prepaidFittingQuantity: number; delAmountPaid: number | null; quantityAmountPaid: number | null; invoiceId: number | null; clientPurchaseOrderNo: string | null; isAdjustment: boolean; adjustmentDesc: string | null; note: string | null; }
export interface CreateOrderBody { sizeQuantities: number[]; deliveryChargeCount: number; prepaidFittingQuantity: number; requestedByContactId: number | null; note: string | null; }
export interface CommitOrderBody extends CreateOrderBody { dueDate: string | null; deliveryMethodId: number | null; deliveryAddressId: number | null; deliveryTo: string | null; deliveryAttention: string | null; doNotLabelAsAllmark: boolean; clientPurchaseOrderNo: string | null; }
export interface PriceOrderBody { sizeQuantities: number[]; deliveryChargeCount: number; prepaidFittingQuantity: number; deliveryMethodId: number | null; }
export interface UpsertDatasheetTypeBody { description: string; productTypeCode: string | null; defaultJobStatusCode: string | null; addOrderToWorkQueueCode: string | null; assignTaskToEmployeeId: number | null; }
export interface IssueStatementBody { deliveryMethod: "Pdf" | "Email"; emailTo: string | null; emailCc: string | null; emailBcc: string | null; message: string | null; from: string | null; to: string | null; }

// ── Presentation helpers ────────────────────────────────────────────────────
/// The mode badge is the first thing a user needs — legacy makes you open the record to find out.
export function modeLabel(mode: string): string {
  switch (mode) {
    case "prepaid": return "Prepaid";
    case "clientStock": return "Client stock";
    case "clientStockPrepaid": return "Client stock · Prepaid";
    case "plain": return "Priced";
    default: return mode;
  }
}

const ifMatch = (rv: string | null) => (rv ? { "If-Match": rv } : ({} as Record<string, string>));

const qs = (entries: Record<string, string | number | boolean | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(entries)) {
    if (v === undefined || v === "" || v === null) continue;
    s.set(k, String(v));
  }
  return s.toString();
};

export const datasheetsApi = {
  list: (p: { companyId?: number; mode?: string; typeCode?: string; q?: string; page?: number; pageSize?: number }) =>
    apiFetch<DatasheetPage>(`/api/datasheets?${qs({
      companyId: p.companyId, mode: p.mode, typeCode: p.typeCode, q: p.q,
      page: p.page ?? 1, pageSize: p.pageSize ?? 50,
    })}`),

  get: (id: number) => apiFetch<DatasheetDetail>(`/api/datasheets/${id}`),
  create: (body: CreateDatasheetBody) => apiFetch<DatasheetDetail>("/api/datasheets", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: UpdateDatasheetBody, rv: string | null) =>
    apiFetch<DatasheetDetail>(`/api/datasheets/${id}`, { method: "PATCH", body: JSON.stringify(body), headers: ifMatch(rv) }),
  remove: (id: number, rv: string | null) =>
    apiFetch<void>(`/api/datasheets/${id}`, { method: "DELETE", headers: ifMatch(rv) }),

  purchases: (id: number) => apiFetch<DatasheetPurchase[]>(`/api/datasheets/${id}/purchases`),
  addPurchase: (id: number, body: CreatePurchaseBody) =>
    apiFetch<DatasheetPurchase>(`/api/datasheets/${id}/purchases`, { method: "POST", body: JSON.stringify(body) }),

  authContacts: (id: number) => apiFetch<DatasheetAuthContact[]>(`/api/datasheets/${id}/auth-contacts`),
  addAuthContact: (id: number, clientId: number) =>
    apiFetch<DatasheetAuthContact>(`/api/datasheets/${id}/auth-contacts`, { method: "POST", body: JSON.stringify({ clientId }) }),
  removeAuthContact: (id: number, clientId: number) =>
    apiFetch<void>(`/api/datasheets/${id}/auth-contacts/${clientId}`, { method: "DELETE" }),

  rates: (id: number) => apiFetch<DatasheetRates>(`/api/datasheets/${id}/rates`),
  pricePreview: (id: number, body: PriceOrderBody) =>
    apiFetch<OrderPricing>(`/api/datasheets/${id}/orders/price-preview`, { method: "POST", body: JSON.stringify(body) }),

  orders: (id: number, committed?: boolean) =>
    apiFetch<DatasheetOrder[]>(`/api/datasheets/${id}/orders?${qs({ committed })}`),
  createOrder: (id: number, body: CreateOrderBody) =>
    apiFetch<DatasheetOrder>(`/api/datasheets/${id}/orders`, { method: "POST", body: JSON.stringify(body) }),
  commitOrder: (id: number, usageId: number, body: CommitOrderBody) =>
    apiFetch<CommitOrderResult>(`/api/datasheets/${id}/orders/${usageId}/commit`, { method: "POST", body: JSON.stringify(body) }),
  derivePreview: (id: number, body: DeriveOrderBody) =>
    apiFetch<DerivedOrderQuantities>(`/api/datasheets/${id}/orders/derive-preview`, { method: "POST", body: JSON.stringify(body) }),
  buildOrder: (id: number, body: BuildOrderBody) =>
    apiFetch<BuildOrderResult>(`/api/datasheets/${id}/orders/build`, { method: "POST", body: JSON.stringify(body) }),
  orderBadges: (id: number, usageId: number) =>
    apiFetch<OrderBadge[]>(`/api/datasheets/${id}/orders/${usageId}/badges`),
  importBadges: (id: number, templateId: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    form.append("templateId", String(templateId));
    return apiFetch<OrderBadgeInput[]>(`/api/datasheets/${id}/orders/import-badges`, { method: "POST", body: form });
  },

  statement: (id: number, from?: string, to?: string) =>
    apiFetch<DatasheetStatement>(`/api/datasheets/${id}/statement?${qs({ from, to })}`),
  issueStatement: (id: number, body: IssueStatementBody) =>
    apiFetch<IssueStatementResult>(`/api/datasheets/${id}/statement/issue`, { method: "POST", body: JSON.stringify(body) }),
  statementPdfUrl: (id: number, from?: string, to?: string) =>
    `/api/datasheets/${id}/statement/pdf?${qs({ from, to })}`,

  sizeRows: (p: { companyId?: number; typeCode?: string; hasRemaining?: boolean }) =>
    apiFetch<DatasheetSizeRow[]>(`/api/datasheet-sizes?${qs(p)}`),

  types: () => apiFetch<DatasheetType[]>("/api/datasheet-types"),
  upsertType: (code: string, body: UpsertDatasheetTypeBody) =>
    apiFetch<DatasheetType>(`/api/datasheet-types/${code}`, { method: "PUT", body: JSON.stringify(body) }),
  removeType: (code: string) => apiFetch<void>(`/api/datasheet-types/${code}`, { method: "DELETE" }),
};

/// Fetches the statement PDF with auth (a raw <a href> cannot attach the Bearer token) and triggers
/// a browser download, mirroring dailyReconApi.downloadPdf / downloadJobFile.
export async function downloadDatasheetStatementPdf(id: number, from?: string, to?: string): Promise<void> {
  const blob = await apiFetchBlob(datasheetsApi.statementPdfUrl(id, from, to));
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `DatasheetStatement-${id}.pdf`;
  a.rel = "noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after 60s so the browser has time to process the click.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ── Hooks ───────────────────────────────────────────────────────────────────
export const datasheetKeys = {
  list: (p: unknown) => ["datasheets", "list", p] as const,
  detail: (id: number) => ["datasheets", "detail", id] as const,
  purchases: (id: number) => ["datasheets", "purchases", id] as const,
  authContacts: (id: number) => ["datasheets", "auth-contacts", id] as const,
  orders: (id: number, committed?: boolean) => ["datasheets", "orders", id, committed] as const,
  orderBadges: (id: number, usageId: number) => ["datasheets", "order-badges", id, usageId] as const,
  statement: (id: number, from?: string, to?: string) => ["datasheets", "statement", id, from, to] as const,
  rates: (id: number) => ["datasheets", "rates", id] as const,
  sizeRows: (p: unknown) => ["datasheets", "size-rows", p] as const,
  types: () => ["datasheets", "types"] as const,
};

export const useDatasheets = (p: Parameters<typeof datasheetsApi.list>[0]) =>
  useQuery({ queryKey: datasheetKeys.list(p), queryFn: () => datasheetsApi.list(p) });

export const useDatasheet = (id: number) =>
  useQuery({ queryKey: datasheetKeys.detail(id), queryFn: () => datasheetsApi.get(id), enabled: id > 0 });

export const useDatasheetPurchases = (id: number) =>
  useQuery({ queryKey: datasheetKeys.purchases(id), queryFn: () => datasheetsApi.purchases(id), enabled: id > 0 });

export const useDatasheetAuthContacts = (id: number) =>
  useQuery({ queryKey: datasheetKeys.authContacts(id), queryFn: () => datasheetsApi.authContacts(id), enabled: id > 0 });

export const useDatasheetOrders = (id: number, committed?: boolean) =>
  useQuery({ queryKey: datasheetKeys.orders(id, committed), queryFn: () => datasheetsApi.orders(id, committed), enabled: id > 0 });

export const useOrderBadges = (id: number, usageId: number, enabled: boolean) =>
  useQuery({ queryKey: datasheetKeys.orderBadges(id, usageId), queryFn: () => datasheetsApi.orderBadges(id, usageId), enabled: enabled && id > 0 && usageId > 0 });

export const useDatasheetStatement = (id: number, from?: string, to?: string) =>
  useQuery({ queryKey: datasheetKeys.statement(id, from, to), queryFn: () => datasheetsApi.statement(id, from, to), enabled: id > 0 });

export const useDatasheetRates = (id: number) =>
  useQuery({ queryKey: datasheetKeys.rates(id), queryFn: () => datasheetsApi.rates(id), enabled: id > 0 });

export const useDatasheetSizeRows = (p: Parameters<typeof datasheetsApi.sizeRows>[0]) =>
  useQuery({ queryKey: datasheetKeys.sizeRows(p), queryFn: () => datasheetsApi.sizeRows(p) });

export const useDatasheetTypes = () =>
  useQuery({ queryKey: datasheetKeys.types(), queryFn: () => datasheetsApi.types() });

/// Anything that moves a balance invalidates the detail, the ledgers and the statement together —
/// a stale balance beside a fresh statement is exactly the confusion the drift warning exists for.
export const useInvalidateDatasheet = () => {
  const qc = useQueryClient();
  return (id: number) => {
    void qc.invalidateQueries({ queryKey: datasheetKeys.detail(id) });
    void qc.invalidateQueries({ queryKey: datasheetKeys.purchases(id) });
    void qc.invalidateQueries({ queryKey: datasheetKeys.authContacts(id) });
    void qc.invalidateQueries({ queryKey: ["datasheets", "orders", id] });
    void qc.invalidateQueries({ queryKey: ["datasheets", "statement", id] });
    void qc.invalidateQueries({ queryKey: ["datasheets", "list"] });
  };
};

export const useCreateDatasheet = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDatasheetBody) => datasheetsApi.create(body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["datasheets", "list"] }); },
  });
};

export const useUpdateDatasheet = (id: number) => {
  const invalidate = useInvalidateDatasheet();
  return useMutation({
    mutationFn: (v: { body: UpdateDatasheetBody; rowVersion: string | null }) =>
      datasheetsApi.update(id, v.body, v.rowVersion),
    onSuccess: () => invalidate(id),
  });
};

export const useDeleteDatasheet = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rowVersion: string | null) => datasheetsApi.remove(id, rowVersion),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["datasheets", "list"] }); },
  });
};

export const useAddAuthContact = (id: number) => {
  const invalidate = useInvalidateDatasheet();
  return useMutation({
    mutationFn: (clientId: number) => datasheetsApi.addAuthContact(id, clientId),
    onSuccess: () => invalidate(id),
  });
};

export const useRemoveAuthContact = (id: number) => {
  const invalidate = useInvalidateDatasheet();
  return useMutation({
    mutationFn: (clientId: number) => datasheetsApi.removeAuthContact(id, clientId),
    onSuccess: () => invalidate(id),
  });
};

export const useIssueStatement = (id: number) => {
  const invalidate = useInvalidateDatasheet();
  return useMutation({
    mutationFn: (body: IssueStatementBody) => datasheetsApi.issueStatement(id, body),
    onSuccess: () => invalidate(id),
  });
};

export const useUpsertDatasheetType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { code: string; body: UpsertDatasheetTypeBody }) => datasheetsApi.upsertType(v.code, v.body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: datasheetKeys.types() }); },
  });
};

export const useDeleteDatasheetType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => datasheetsApi.removeType(code),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: datasheetKeys.types() }); },
  });
};
